import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSparkRunsStatusTypes } from 'hooks/EntityTypes/useSparkRunsStatusTypes';
import { useSparkRuns } from 'hooks/SparkRuns/useSparkRuns';
import HistoryCardSkeleton from 'sharedComponents/Skeleton/HistoryCardSkeleton';
import { formatDateAndTime } from 'utilities/time';
import { HistoryFilter } from 'components/History';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import { InfoCard, SparkDataTable } from 'sharedComponents/UI';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { DefaultUserIconFilled } from 'components/SVG';
import StatusesWrapper from 'sharedComponents/UI/StatusesWrapper';
import { formatCredits } from 'utilities/index';
import { SPARK_ROUTES } from 'constants/routes';
import { PaginationQuery } from 'utilities/hooks/urlGeneration';
import Pagination from 'sharedComponents/Pagination';
import { SparkImageLoader } from 'sharedComponents/UI';

type SparkRunsHistoryProps = {
  isParentLoading?: boolean;
  isParentFetching?: boolean;
  sparkID?: string;
  historyFilter?: HistoryFilter;
  selectedStatus?: string;
  showPaginationControls?: boolean;
  paginatedQuery?: PaginationQuery;
  onNoSparkRuns?: () => void;
  showAvatarAndName?: boolean;
  boldDate?: boolean;
  runIdFilter?: string;
  isMarketplace?: boolean;
};

type SparkRunsHistoryRow = {
  id: string;
  status: string;
  iconUrl: string;
  name: string;
  dateTime: string;
  credits: string;
  imageUrl: string;
};

function SparkRunsHistory({
  isParentLoading = false,
  isParentFetching = false,
  sparkID,
  historyFilter,
  selectedStatus,
  showPaginationControls = false,
  paginatedQuery,
  onNoSparkRuns,
  showAvatarAndName = false,
  boldDate = false,
  runIdFilter = '',
  isMarketplace = false,
}: SparkRunsHistoryProps) {
  const navigate = useNavigate();
  const [currentPagination, setCurrentPagination] =
    React.useState<PaginationQuery>({ pageNumber: 1, pageSize: 10 });

  const {
    idMap,
    isLoading: isLoadingSparkRunsStatusTypes,
    isFetching: isFetchingSparkRunsStatusTypes,
  } = useSparkRunsStatusTypes();

  const getPagination = () => {
    if (showPaginationControls) {
      return currentPagination;
    }
    return !!paginatedQuery ? paginatedQuery : undefined;
  };

  const getSparkIDs = () => {
    let sparkIDs: string[] = [];
    if (sparkID) {
      sparkIDs.push(sparkID);
    }
    if (historyFilter?.sparkIDs) {
      sparkIDs = [...sparkIDs, ...historyFilter?.sparkIDs];
    }
    return sparkIDs;
  };
  const {
    sparkRunsHistory,
    total,
    isLoading: isLoadingSparkRunsHistory,
    isFetching: isFetchingSparkRunsHistory,
  } = useSparkRuns({
    sparkIDs: getSparkIDs(),
    pagination: getPagination(),
    historyDateRangeFilter: historyFilter?.dateRange,
    statusFilter: selectedStatus === 'all' ? undefined : selectedStatus,
  });

  const isLoading =
    isParentLoading ||
    isLoadingSparkRunsStatusTypes ||
    isLoadingSparkRunsHistory;
  const isFetching =
    isParentFetching ||
    isFetchingSparkRunsStatusTypes ||
    isFetchingSparkRunsHistory;

  const columns: Column<SparkRunsHistoryRow>[] = [
    {
      field: 'status',
      headerText: 'Status',
      filterAble: true,
      sortable: true,
      flex: 1,
      cellRenderer: (row) => {
        const status = idMap?.[row.status || ''];
        if (!status) {
          return <></>;
        }
        return (
          <Box display='flex' alignItems='center'>
            <StatusesWrapper
              iconUrl={status.attributes.iconURL}
              name={status.attributes.name}
              statusColor={status.attributes.textColour}
            />
          </Box>
        );
      },
    },
    {
      field: 'name',
      headerText: 'Name',
      flex: 3,
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            {showAvatarAndName && (
              <SparkAvatarBackground
                src={row.iconUrl || ''}
                icon={<SparkdefaultIcon />}
                width='32px'
                height='32px'
              />
            )}

            <Box>
              {showAvatarAndName && (
                <Typography
                  variant='body1'
                  sx={{
                    color: '#000000',
                  }}
                >
                  {row.name || '- -'}
                </Typography>
              )}
              <Typography
                variant='body2'
                sx={{
                  color: showAvatarAndName ? '#BDBDBD' : '#000000',
                  fontWeight: showAvatarAndName ? 'normal' : 'bold',
                  pt: 0.5,
                }}
              >
                {formatDateAndTime(row.dateTime || '')}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'credits',
      headerText: 'Credits',
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <Box textAlign='center'>
              {!!row.imageUrl ? (
                <SparkImageLoader
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                  }}
                  src={row.imageUrl}
                  alt={row.imageUrl}
                  loading='lazy'
                />
              ) : (
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    backgroundColor: '#6267AC',
                  }}
                >
                  <DefaultUserIconFilled width='40' height='40' />
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
  ];

  const rows: SparkRunsHistoryRow[] | undefined = sparkRunsHistory?.map(
    (sparkRun) => {
      const credits = formatCredits(sparkRun?.meta.credits);

      return {
        id: sparkRun.id,
        status: `${sparkRun.attributes.sparkRunStatusTypeID || ''}`,
        iconUrl: sparkRun.spark?.attributes.iconURL || '',
        name: sparkRun.spark?.attributes.nameforHuman || '',
        dateTime: formatDateAndTime(sparkRun.attributes.createdAt) || '',
        credits: credits + '',
        imageUrl: sparkRun.applicationUser?.attributes.profilePictureURL || '',
      };
    }
  );

  // Filter the sparkRunsRows by runIdFilter if provided
  const filteredRows = React.useMemo(() => {
    if (!rows) {
      return [];
    }

    let filtered = [...rows];

    // Filter by runId if provided
    if (runIdFilter) {
      filtered = filtered.filter((row) =>
        row.id.toLowerCase().includes(runIdFilter.toLowerCase())
      );
    }

    return filtered;
  }, [rows, runIdFilter]);

  // Use filteredRows instead of rows for rendering
  const hasRows = filteredRows && filteredRows.length > 0;

  React.useEffect(() => {
    if (rows?.length === 0 && onNoSparkRuns) {
      onNoSparkRuns();
    }
  }, [rows, onNoSparkRuns]);
  return (
    <Grid container gap={0} paddingBottom={0}>
      {isLoading || isFetching ? (
        <HistoryCardSkeleton />
      ) : filteredRows && filteredRows.length === 0 ? (
        <Box width={'100%'}>
          <InfoCard info='No Spark Runs found' />
        </Box>
      ) : (
        <>
          <SparkDataTable<SparkRunsHistoryRow>
            columns={columns}
            rows={filteredRows || []}
            onRowClick={(rowID) =>
              navigate(`${SPARK_ROUTES.SPARK_RUN_DETAIL}/${rowID}`)
            }
            pagination={false}
            shouldRenderHeader={false}
          />
          {showPaginationControls && (
            <Pagination
              itemsPerPage={getPagination()?.pageSize || 0}
              dataCount={total}
              currentPage={getPagination()?.pageNumber || 1}
              setCurrentPage={(pageNumber) =>
                setCurrentPagination((prev) => ({
                  ...prev,
                  pageNumber: pageNumber,
                }))
              }
              gridProps={{ marginTop: 1 }}
            />
          )}
        </>
      )}
    </Grid>
  );
}

export default SparkRunsHistory;
